// Ref-search picker (Story #4613, Stream 8c) — the shared picker behind
// "Mark as duplicate of…" and "Link related ticket". Searches the admin queue
// (open + closed) by ref/subject/requester and returns the chosen ticket id.

import { useEffect, useMemo, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'
import { adminListTickets } from '../../../services/support'
import { SUPPORT_PORTAL } from '../../../constants/support'
import type { AdminSupportTicket } from '../../../types/support'

interface RefSearchPickerProps {
  title: string
  /** ticket ids to exclude (self + already-related). */
  excludeIds: string[]
  confirmLabel: string
  onPick: (ticket: AdminSupportTicket) => Promise<void>
  onClose: () => void
}

export default function RefSearchPicker({ title, excludeIds, confirmLabel, onPick, onClose }: RefSearchPickerProps) {
  const [all, setAll] = useState<AdminSupportTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState<AdminSupportTicket | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    Promise.all([
      adminListTickets({ portal: SUPPORT_PORTAL, status: 'open' }, ctrl.signal),
      adminListTickets({ portal: SUPPORT_PORTAL, status: 'closed' }, ctrl.signal),
    ])
      .then(([open, closed]) => {
        const merged = [...(open.tickets ?? []), ...(closed.tickets ?? [])]
        setAll(merged)
        setLoading(false)
      })
      .catch((e) => {
        if ((e as Error)?.name === 'AbortError') return
        setError(e instanceof Error ? e.message : 'Could not load tickets')
        setLoading(false)
      })
    return () => ctrl.abort()
  }, [])

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const exclude = new Set(excludeIds)
    return all
      .filter((t) => !exclude.has(t.ticket_id))
      .filter((t) =>
        needle
          ? `${t.ref} ${t.subject} ${t.user_id ?? ''}`.toLowerCase().includes(needle)
          : true,
      )
      .slice(0, 25)
  }, [all, q, excludeIds])

  async function confirm() {
    if (!selected) return
    setBusy(true)
    setError(null)
    try {
      await onPick(selected)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed — refresh and retry')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => !busy && onClose()} aria-hidden />
      <div className="relative flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-iflow-900">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" disabled={busy} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-300 px-2.5 py-1.5">
          <Search size={15} className="text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search ref, subject, requester…"
            className="w-full text-[13px] outline-none"
            autoFocus
          />
        </div>

        <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-lg border border-slate-100">
          {loading ? (
            <div className="flex items-center gap-2 p-4 text-slate-500">
              <Loader2 className="animate-spin" size={16} /> Loading…
            </div>
          ) : results.length === 0 ? (
            <p className="p-4 text-[13px] text-slate-500">No matching tickets.</p>
          ) : (
            <ul>
              {results.map((t) => (
                <li key={t.ticket_id}>
                  <button
                    type="button"
                    onClick={() => setSelected(t)}
                    className={`flex w-full flex-col items-start gap-0.5 border-b border-slate-100 px-3 py-2 text-left text-[12.5px] hover:bg-iflow-50 ${
                      selected?.ticket_id === t.ticket_id ? 'bg-iflow-100' : ''
                    }`}
                  >
                    <span className="font-semibold text-iflow-600">{t.ref}</span>
                    <span className="truncate text-slate-700">{t.subject}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!selected || busy}
            className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-700 disabled:opacity-60"
          >
            {busy && <Loader2 size={15} className="animate-spin" />} {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
